/**
 * TypeScript counterparts to the shared KMP domain types in
 * `shared/.../domain/Coordinate.kt`. Lyktan is TypeScript-only so it does
 * not consume the KMP-shared module directly; these types mirror the JSON
 * shape that the backend emits and accepts so the wire format stays in sync.
 *
 * Hard rules:
 * - Coordinates are WGS84 (EPSG:4326).
 * - A `Polyline` always has at least two points (matches Kotlin `init` check).
 * - No free-text fields: the drawing surface stores only geometry, mode and
 *   waypoints — never user-supplied notes (those are admin-only on the
 *   detail panel per UI-kontrakt vy 7, not on the drawing surface).
 */

/** Single point on the map. Latitude in [-90, 90], longitude in [-180, 180]. */
export interface Coordinate {
  readonly latitude: number;
  readonly longitude: number;
}

/**
 * One contiguous line segment. A hearth's geometry is a list of
 * `Polyline` (MULTILINESTRING-equivalent on the backend / PostGIS side).
 * MVP1 only emits a single polyline per drawing session; multi-segment
 * geometry composition lives on the save endpoint (S-04-06).
 */
export interface Polyline {
  readonly points: ReadonlyArray<Coordinate>;
}

/**
 * Drawing modes supported by the hearth-drawing surface.
 *
 * - `snap-to-road` — every click adds a waypoint, the backend route proxy
 *   (`POST /api/admin/route`) returns a polyline that follows actual roads
 *   and walking paths via GraphHopper. Default mode.
 * - `free` — every click adds a vertex directly, no road snapping. Used
 *   for places where OSM lacks coverage (parks, footpaths between
 *   buildings). Implemented in S-04-05.
 */
export type DrawingMode = 'snap-to-road' | 'free';

export const DEFAULT_DRAWING_MODE: DrawingMode = 'snap-to-road';

/**
 * Validate that a point is inside WGS84 ranges. Used at the boundary
 * (route service, click handlers) so invalid input fails fast with a
 * predictable error before it reaches the backend or gets serialised.
 */
export function isValidCoordinate(c: Coordinate): boolean {
  return (
    Number.isFinite(c.latitude) &&
    Number.isFinite(c.longitude) &&
    c.latitude >= -90 &&
    c.latitude <= 90 &&
    c.longitude >= -180 &&
    c.longitude <= 180
  );
}
